import { Box, Grid, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import React, { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DelendSignOnButton from "../../../components/Buttons/DelendSignOnButton";
import UserService from "../../../services/UserService";
import MixPanelMetrics from "../../../tools/MixPanelMetrics";
import { LegalPersonInput, legalPersonSchema } from "../validation";
import AgreementCheck from "./components/AgreementCheck";
import CnpjInput from "./components/CnpjInput";
import CorporateNameInput from "./components/CorporateNameInput";
import CpfInput from "./components/CpfInput";
import EmailInput from "./components/EmailInput";
import LegalRepresentativeCheck from "./components/LegalRepresentativeCheck";
import MobilePhoneInput from "./components/MobilePhoneInput";
import PasswordConfirmationInput from "./components/PasswordConfirmationInput";
import PasswordInput from "./components/PasswordInput";
import ResponsibleNameInput from "./components/ResponsibleNameInput";

type PasswordButtonsType = {
  showPassword: boolean;
  showPasswordConfirmation: boolean;
};

const LegalPerson: React.FC = () => {
  const {
    register,
    resetField,
    getValues,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<LegalPersonInput>({
    resolver: zodResolver(legalPersonSchema),
  });

  async function executeLogin(email: string, password: string) {
    const user = {
      username: email,
      password: password,
    };
    await UserService.login(user);
    window.location.href = "/dashboard";
  }

  const onSubmit: SubmitHandler<LegalPersonInput> = async (
    userRegisterData
  ) => {
    try {
      const response = await UserService.signUpLegalPerson(userRegisterData);

      if (response.status === 201) {
        await executeLogin(userRegisterData.email, userRegisterData.password);
        MixPanelMetrics.sendEvent({
          event: "SignUp",
          eventName: "Criação de conta",
          createdAt: new Date().toISOString(),
          personUuid: "",
          subscriptionUuid: "",
          properties: {
            isSuccess: true,
            cnpj: userRegisterData.documentNumber,
            cpf: userRegisterData.responsibleCpf,
            email: userRegisterData.email,
            name: userRegisterData.responsibleName,
            username: userRegisterData.email,
            platform: window.navigator.userAgent,
          },
        });
      }
    } catch (error: any) {
      toast.error(error.response.data.message);
      MixPanelMetrics.sendEvent({
        event: "SignUp",
        eventName: "Criação de conta",
        createdAt: new Date().toISOString(),
        personUuid: "",
        subscriptionUuid: "",
        properties: {
          isSuccess: false,
          cnpj: userRegisterData.documentNumber,
          cpf: userRegisterData.responsibleCpf,
          email: userRegisterData.email,
          name: userRegisterData.responsibleName,
          username: userRegisterData.email,
          platform: window.navigator.userAgent,
        },
      });
      Sentry.captureException(error);
    }
  };

  const [passwordButtons, setPasswordButtons] = useState<PasswordButtonsType>({
    showPassword: false,
    showPasswordConfirmation: false,
  });

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2} sx={{ marginTop: "32px" }}>
        <Grid item xs={12}>
          <CorporateNameInput register={register} errors={errors} />
        </Grid>
        <Grid item xs={12}>
          <CnpjInput register={register} errors={errors} />
        </Grid>
        <Grid item xs={12}>
          <ResponsibleNameInput register={register} errors={errors} />
        </Grid>
        <Grid container item columnSpacing={2}>
          <Grid item xs={12} lg={6}>
            <CpfInput
              register={register}
              errors={errors}
              getValues={getValues}
              resetField={resetField}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <MobilePhoneInput register={register} errors={errors} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <EmailInput register={register} errors={errors} />
        </Grid>
        <Grid container item columnSpacing={2}>
          <Grid item xs={12} lg={6}>
            <PasswordInput
              register={register}
              errors={errors}
              label="Senha"
              passwordButtons={passwordButtons}
              setPasswordButtons={setPasswordButtons}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <PasswordConfirmationInput
              register={register}
              errors={errors}
              label="Confirma Senha"
              passwordButtons={passwordButtons}
              setPasswordButtons={setPasswordButtons}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <LegalRepresentativeCheck register={register} errors={errors} />
        </Grid>
        <Grid item xs={12}>
          <AgreementCheck register={register} errors={errors} />
        </Grid>
      </Grid>

      <Box
        sx={{ marginTop: "32px" }}
        display={"flex"}
        justifyContent={"center"}
      >
        <DelendSignOnButton
          onClick={() => {}}
          enableButton={true}
          isLoading={isSubmitting}
        />
      </Box>
      <Box
        sx={{ marginTop: "24px" }}
        display={"flex"}
        justifyContent={"center"}
      >
        <Typography>
          <Link className="createAccount" to="/">
            voltar para o login
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export type { PasswordButtonsType };
export default LegalPerson;
